import React, { useEffect, useState } from "react";
import {
  EuiForm,
  EuiFormRow,
  EuiFieldText,
  EuiTextArea,
  EuiSelect,
  EuiButton,
  EuiSpacer,
  EuiCallOut,
  EuiText,
  EuiTitle,
  EuiPanel,
  EuiFlexGroup,
  EuiFlexItem,
} from "@elastic/eui";
import { useNavigate, useSearchParams } from "react-router-dom";
import Turnstile from "react-turnstile";
import { useAnketaI18n } from "src/internationalization";
import { AutonubilHomepageFooter } from "./footer";
import { useApi } from "src/api";
import { useToast } from "src/notifications";
import { CheckCircle, Send, AlertCircle } from "lucide-react";

interface ContactFormData {
  name: string;
  email: string;
  company: string;
  topic: string;
  message: string;
  captchaToken?: string;
  language?: string;
}

export type ContactTopics =
  | "opsanio"
  | "kubernetes"
  | "docker"
  | "devops"
  | "nextcloud"
  | "keycloak"
  | "automation"
  | "other";

export interface ContactQueryParams {
  baseUrl?: string;
  name?: string;
  email?: string;
  topic?: ContactTopics;
  message?: string | null;
}

export const buildContactUrl = ({
  baseUrl = "/home/contact",
  name,
  email,
  topic,
  message,
}: ContactQueryParams): string => {
  const params = new URLSearchParams();
  if (name) params.set("name", name);
  if (email) params.set("email", email);
  if (topic) params.set("topic", topic);
  if (message) params.set("message", message);
  return `${baseUrl}?${params.toString()}`;
};

// Brand accent only. Everything structural (backgrounds, text, borders) comes
// from theme-aware EUI components so the form renders correctly in light AND
// dark mode (this route is not force-pinned to light like the marketing page).
const ACCENT = "#07a0ae";
const ACCENT_2 = "#78c9d9";
const ACCENT_GRADIENT = `linear-gradient(90deg, ${ACCENT} 0%, ${ACCENT_2} 100%)`;

const siteKey = "0x4AAAAAAB6Unsu1RmHaHD42";

const SuccessScreen: React.FC<{ countdown: number; label: string }> = ({
  countdown,
  label,
}) => (
  <EuiFlexGroup
    direction="column"
    alignItems="center"
    justifyContent="center"
    gutterSize="m"
    style={{ textAlign: "center", padding: "48px 16px" }}
  >
    <EuiFlexItem grow={false}>
      <div
        style={{
          width: 72,
          height: 72,
          borderRadius: "50%",
          background: "rgba(7,160,174,0.12)",
          border: `1px solid ${ACCENT}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CheckCircle size={36} color={ACCENT} />
      </div>
    </EuiFlexItem>
    <EuiFlexItem grow={false}>
      <EuiTitle size="m">
        <h2 style={{ margin: 0 }}>{label}</h2>
      </EuiTitle>
    </EuiFlexItem>
    <EuiFlexItem grow={false}>
      <EuiText size="xs" color="subdued">
        <span style={{ fontFamily: `"JetBrains Mono","Fira Code",monospace` }}>
          {countdown}s
        </span>
      </EuiText>
    </EuiFlexItem>
    <EuiFlexItem grow={false}>
      <div
        style={{
          width: 180,
          height: 3,
          background: "rgba(127,127,127,0.25)",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            background: ACCENT_GRADIENT,
            borderRadius: 2,
            width: `${(5 - countdown) * 20}%`,
            transition: "width 0.9s linear",
          }}
        />
      </div>
    </EuiFlexItem>
  </EuiFlexGroup>
);

export const AutonubilContactForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { translate, currentLang } = useAnketaI18n();
  const { Post } = useApi();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    company: "",
    topic: "",
    message: "",
    captchaToken: null,
    language: currentLang,
  });

  const [submitted, setSubmitted] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [errors, setErrors] = useState<
    Partial<Record<keyof ContactFormData, string>>
  >({});
  const [touched, setTouched] = useState<
    Partial<Record<keyof ContactFormData, boolean>>
  >({});
  const [submitting, setSubmitting] = useState(false);

  const topics: { value: ContactTopics | ""; text: string }[] = [
    {
      value: "",
      text: translate("anketa.autonubil.contact.topic.select", {
        defaultMessage: "Please select…",
      }),
    },
    {
      value: "opsanio",
      text: translate("anketa.autonubil.home.appliance.title"),
    },
    {
      value: "kubernetes",
      text: translate("anketa.documentation.opsanio.toc.sections.kubernetes"),
    },
    { value: "docker", text: translate("anketa.autonubil.homepage.docker") },
    {
      value: "devops",
      text: translate("anketa.autonubil.home.services.devops.title"),
    },
    {
      value: "nextcloud",
      text: translate("anketa.autonubil.home.services.nextcloud.title"),
    },
    {
      value: "keycloak",
      text: translate("anketa.autonubil.home.services.keycloak.title"),
    },
    {
      value: "automation",
      text: translate("anketa.autonubil.homepage.automation"),
    },
    { value: "other", text: translate("anketa.autonubil.contact.topic.other") },
  ];

  useEffect(() => {
    const urlTopic = searchParams.get("topic") as ContactTopics | null;
    const validTopic = topics.some((t) => t.value === urlTopic) ? urlTopic : "";
    setFormData({
      name: searchParams.get("name") || "",
      email: searchParams.get("email") || "",
      topic: validTopic,
      company: searchParams.get("company") || "",
      message: searchParams.get("message") || "",
      captchaToken: null,
      language: currentLang,
    });
  }, [searchParams]);

  useEffect(() => {
    if (!submitted) return;
    if (countdown === 0) {
      navigate("/home");
      return;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [submitted, countdown]);

  const validateField = (
    field: keyof ContactFormData,
    value: string | null,
  ): string => {
    switch (field) {
      case "name":
        if (!value || value.trim().length < 2)
          return translate("anketa.autonubil.contact.validation.name", {
            defaultMessage: "Please enter your name (at least 2 characters)",
          });
        break;
      case "email":
        if (!value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
          return translate("anketa.autonubil.contact.validation.email", {
            defaultMessage: "Please enter a valid e-mail address",
          });
        break;
      case "company":
        if (!value || value.trim().length < 2)
          return translate("anketa.autonubil.contact.validation.company", {
            defaultMessage: "Please enter your company",
          });
        break;
      case "topic":
        if (!value || value === "")
          return translate("anketa.autonubil.contact.validation.topic", {
            defaultMessage: "Please select a topic",
          });
        break;
      case "message":
        if (!value || value.trim().length < 10)
          return translate("anketa.autonubil.contact.validation.message", {
            defaultMessage: "Please enter a message (at least 10 characters)",
          });
        break;
      case "captchaToken":
        if (!value)
          return translate("anketa.autonubil.contact.validation.captcha", {
            defaultMessage: "Please confirm the captcha",
          });
        break;
    }
    return "";
  };

  const handleChange = (field: keyof ContactFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (touched[field])
      setErrors((prev) => ({ ...prev, [field]: validateField(field, value) }));
  };

  const handleBlur = (field: keyof ContactFormData) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    setErrors((prev) => ({
      ...prev,
      [field]: validateField(
        field,
        field === "captchaToken" ? formData.captchaToken : formData[field],
      ),
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof ContactFormData, string>> = {};
    let isValid = true;
    (Object.keys(formData) as (keyof ContactFormData)[]).forEach((field) => {
      if (field !== "language") {
        const err = validateField(field, formData[field]);
        if (err) {
          newErrors[field] = err;
          isValid = false;
        }
      }
    });
    setErrors(newErrors);
    setTouched({
      name: true,
      email: true,
      company: true,
      topic: true,
      message: true,
      captchaToken: true,
    });
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      addToast({
        title: translate("anketa.autonubil.contact.toast.validation.title", {
          defaultMessage: "Form incomplete",
        }),
        color: "warning",
        text: translate("anketa.autonubil.contact.toast.validation.text", {
          defaultMessage: "Please fill in all required fields correctly.",
        }),
      });
      return;
    }
    setSubmitting(true);
    try {
      await Post<string>({
        path: "v2/autonubil/contact",
        responseType: "json",
        value: formData,
      });
      addToast({
        title: translate("anketa.autonubil.contact.toast.success.title", {
          defaultMessage: "Message sent",
        }),
        color: "success",
        text: translate("anketa.autonubil.contact.toast.success.text", {
          defaultMessage: "Your message was delivered. We'll be in touch soon.",
        }),
      });
      setFormData({
        name: "",
        email: "",
        company: "",
        topic: "",
        message: "",
        captchaToken: null,
        language: currentLang,
      });
      setSubmitted(true);
    } catch {
      addToast({
        title: translate("anketa.autonubil.contact.toast.error.title", {
          defaultMessage: "Error sending message",
        }),
        color: "danger",
        text: translate("anketa.autonubil.contact.toast.error.text", {
          defaultMessage: "Your message could not be sent. Please try again.",
        }),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    // No hardcoded page background: inherits the theme-aware body background so
    // the page is correct in both light and dark mode.
    <div style={{ minHeight: "100vh" }}>
      <div
        style={{
          maxWidth: 800,
          margin: "0 auto",
          padding: "72px clamp(24px, 5vw, 48px) 80px",
        }}
      >
        <div style={{ marginBottom: 40 }}>
          <div
            style={{
              fontSize: "0.7rem",
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: ACCENT,
              marginBottom: 10,
            }}
          >
            {translate("anketa.autonubil.contact.eyebrow", {
              defaultMessage: "Get in touch",
            } as any)}
          </div>
          <EuiTitle size="l">
            <h1 style={{ fontWeight: 800, letterSpacing: "-0.02em", margin: 0 }}>
              {translate("anketa.autonubil.contact.title")}
            </h1>
          </EuiTitle>
          <div
            style={{
              width: 56,
              height: 3,
              marginTop: 14,
              marginBottom: 16,
              background: ACCENT_GRADIENT,
              borderRadius: 2,
            }}
          />
          <EuiText color="subdued">
            <p style={{ margin: 0, fontSize: "0.97rem", lineHeight: 1.75 }}>
              {translate("anketa.autonubil.contact.subtitle")}
            </p>
          </EuiText>
        </div>

        <EuiPanel
          hasShadow
          hasBorder
          paddingSize="none"
          style={{ borderRadius: 12, overflow: "hidden" }}
        >
          {/* Brand accent bar (works on both themes) */}
          <div style={{ height: 3, background: ACCENT_GRADIENT }} />

          <div style={{ padding: "clamp(24px,4vw,40px)" }}>
            {submitted ? (
              <SuccessScreen
                countdown={countdown}
                label={translate(
                  "anketa.autonubil.contact.toast.success.title",
                  { defaultMessage: "Message sent successfully" },
                )}
              />
            ) : (
              <EuiForm component="form" onSubmit={handleSubmit}>
                {/* Responsive rows: two columns on wide screens, stacked on mobile */}
                <EuiFlexGroup gutterSize="m">
                  <EuiFlexItem>
                    <EuiFormRow
                      label={translate("anketa.autonubil.contact.name")}
                      isInvalid={touched.name && !!errors.name}
                      error={errors.name}
                      fullWidth
                    >
                      <EuiFieldText
                        value={formData.name}
                        onChange={(e) => handleChange("name", e.target.value)}
                        onBlur={() => handleBlur("name")}
                        placeholder={translate(
                          "anketa.autonubil.contact.name.placeholder",
                          { defaultMessage: "Max Mustermann" },
                        )}
                        isInvalid={touched.name && !!errors.name}
                        fullWidth
                      />
                    </EuiFormRow>
                  </EuiFlexItem>
                  <EuiFlexItem>
                    <EuiFormRow
                      label={translate("anketa.autonubil.contact.email")}
                      isInvalid={touched.email && !!errors.email}
                      error={errors.email}
                      fullWidth
                    >
                      <EuiFieldText
                        value={formData.email}
                        onChange={(e) => handleChange("email", e.target.value)}
                        onBlur={() => handleBlur("email")}
                        type="email"
                        placeholder={translate(
                          "anketa.autonubil.contact.email.placeholder",
                          { defaultMessage: "max@firma.de" },
                        )}
                        isInvalid={touched.email && !!errors.email}
                        fullWidth
                      />
                    </EuiFormRow>
                  </EuiFlexItem>
                </EuiFlexGroup>

                <EuiFlexGroup gutterSize="m">
                  <EuiFlexItem>
                    <EuiFormRow
                      label={translate("anketa.autonubil.contact.company")}
                      isInvalid={touched.company && !!errors.company}
                      error={errors.company}
                      fullWidth
                    >
                      <EuiFieldText
                        value={formData.company}
                        onChange={(e) =>
                          handleChange("company", e.target.value)
                        }
                        onBlur={() => handleBlur("company")}
                        placeholder={translate(
                          "anketa.autonubil.contact.company.placeholder",
                          { defaultMessage: "Your Company" },
                        )}
                        isInvalid={touched.company && !!errors.company}
                        fullWidth
                      />
                    </EuiFormRow>
                  </EuiFlexItem>
                  <EuiFlexItem>
                    <EuiFormRow
                      label={translate("anketa.autonubil.contact.topic.label")}
                      isInvalid={touched.topic && !!errors.topic}
                      error={errors.topic}
                      fullWidth
                    >
                      <EuiSelect
                        options={topics}
                        value={formData.topic}
                        onChange={(e) => handleChange("topic", e.target.value)}
                        onBlur={() => handleBlur("topic")}
                        isInvalid={touched.topic && !!errors.topic}
                        fullWidth
                      />
                    </EuiFormRow>
                  </EuiFlexItem>
                </EuiFlexGroup>

                <EuiFormRow
                  label={translate("anketa.autonubil.contact.message")}
                  isInvalid={touched.message && !!errors.message}
                  error={errors.message}
                  fullWidth
                >
                  <EuiTextArea
                    value={formData.message}
                    onChange={(e) => handleChange("message", e.target.value)}
                    onBlur={() => handleBlur("message")}
                    placeholder={translate(
                      "anketa.autonubil.contact.message.placeholder",
                      { defaultMessage: "Your message…" },
                    )}
                    rows={5}
                    isInvalid={touched.message && !!errors.message}
                    fullWidth
                  />
                </EuiFormRow>

                <EuiSpacer size="m" />

                <EuiFormRow
                  isInvalid={touched.captchaToken && !!errors.captchaToken}
                  error={errors.captchaToken}
                >
                  <Turnstile
                    sitekey={siteKey}
                    onVerify={(token) => {
                      handleChange("captchaToken", token);
                      handleBlur("captchaToken");
                    }}
                  />
                </EuiFormRow>

                {touched.captchaToken && errors.captchaToken && (
                  <>
                    <EuiSpacer size="s" />
                    <EuiCallOut
                      color="warning"
                      size="s"
                      iconType={() => <AlertCircle size={16} />}
                      title={errors.captchaToken}
                    />
                  </>
                )}

                <EuiSpacer size="l" />

                <EuiButton
                  type="submit"
                  fill
                  fullWidth
                  isLoading={submitting}
                  iconType={() => <Send size={15} style={{ marginRight: 4 }} />}
                  style={{
                    background: `linear-gradient(135deg, ${ACCENT} 0%, #0891a5 100%)`,
                    borderColor: ACCENT,
                    fontWeight: 600,
                  }}
                >
                  {translate("anketa.autonubil.contact.submit", {
                    defaultMessage: "Send message",
                  })}
                </EuiButton>
              </EuiForm>
            )}
          </div>
        </EuiPanel>
      </div>

      <AutonubilHomepageFooter />
    </div>
  );
};

export default AutonubilContactForm;
